/**
 * PropitstopHome — the umbrella landing at `/`. Three product tiles
 * driving the visitor to Buy Cars / Service / Estimate Check, followed
 * by trust badges, business partners section, and a "Why choose" stats
 * strip. Layout matches the reference mockup.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import {
  FileText, ShieldCheck, IndianRupee, Sparkles, Lock, Headphones,
  ArrowRight,
} from 'lucide-react';
import PropitstopNav from '../components/PropitstopNav';
import PropitstopFooter from '../components/PropitstopFooter';
import SEO from '../components/SEO';
import { useSiteImages } from '../hooks/useSiteImages';

const HERO_CAR =
  'https://images.unsplash.com/photo-1552519507-da3b142c6e3d?auto=format&fit=crop&w=1600&q=80';
const CAR_TILE_IMG =
  'https://images.unsplash.com/photo-1552519507-da3b142c6e3d?auto=format&fit=crop&w=800&q=80';
const SERVICE_TILE_IMG =
  'https://images.unsplash.com/photo-1632823471565-1ecdf5c0fecc?auto=format&fit=crop&w=800&q=80';
const ESTIMATE_TILE_IMG =
  'https://images.unsplash.com/photo-1600431521340-491eca880813?auto=format&fit=crop&w=800&q=80';

// Default artwork for the Franchise section. Admin can override via
// Site Images → home_franchise_image.
const FRANCHISE_DEFAULT_IMG =
  'https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=1200&q=80';

const TRUST_BADGES = [
  { icon: ShieldCheck, label: 'Trusted & Verified Partners' },
  { icon: IndianRupee, label: 'Transparent Pricing' },
  { icon: Sparkles, label: 'AI-Powered Recommendations' },
  { icon: Lock, label: '100% Secure & Reliable' },
  { icon: Headphones, label: 'Expert Support Always' },
];

const BUSINESS = [
  {
    label: 'Used Car Dealers',
    desc: 'Get more leads, sell faster and manage your business efficiently.',
    live: true,
    // Sky — calm, dealership blue vibe
    bg: 'bg-sky-50 hover:bg-sky-100/70 border-sky-100',
    accent: 'text-sky-700',
  },
  {
    label: 'Workshops',
    desc: 'Streamline operations and deliver exceptional service.',
    live: true,
    // Emerald — trust + service
    bg: 'bg-emerald-50 hover:bg-emerald-100/70 border-emerald-100',
    accent: 'text-emerald-700',
  },
  {
    label: 'Spare Parts Suppliers',
    desc: 'Reach more customers and grow your parts business.',
    live: false,
    // Amber — industrial, mechanical warmth
    bg: 'bg-amber-50 hover:bg-amber-100/70 border-amber-100',
    accent: 'text-amber-700',
  },
  {
    label: 'Accessories Businesses',
    desc: 'Expand your reach and boost your sales.',
    live: false,
    // Rose — creative, retail glow
    bg: 'bg-rose-50 hover:bg-rose-100/70 border-rose-100',
    accent: 'text-rose-700',
  },
];

const STATS_DELETED = null; // "Why choose" stats intentionally removed until we have real numbers to publish.

const POPULAR_HIDDEN = null; // "Popular Searches" chips removed with the search bar.

export default function PropitstopHome() {
  const { imageFor } = useSiteImages();
  const heroCar = imageFor('home_hero_car', HERO_CAR);
  const buyCarsTile = imageFor('home_buy_cars_tile', CAR_TILE_IMG);
  const serviceTile = imageFor('home_service_tile', SERVICE_TILE_IMG);
  const franchiseImg = imageFor('home_franchise_image', FRANCHISE_DEFAULT_IMG);
  return (
    <div className="min-h-screen bg-white text-zinc-900" data-testid="propitstop-home">
      <SEO
        title="Propitstop — Buy Used Cars & Book Car Service in Bangalore | AI-Powered Automotive Platform"
        description="India's AI-powered automotive platform. Buy verified used cars, book trusted car service in Bangalore, and grow your dealership or garage without depending on aggregators. Everything your car needs — in one place."
        canonical={typeof window !== 'undefined' ? `${window.location.origin}/` : ''}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'Propitstop',
          url: 'https://propitstop.in',
          logo: 'https://propitstop.in/propitstop-logo.jpg',
          description: 'India\'s AI-powered automotive platform for buying used cars, booking car service, and running used-car dealerships and garages.',
          sameAs: [],
          address: { '@type': 'PostalAddress', addressLocality: 'Bengaluru', addressRegion: 'KA', addressCountry: 'IN' },
          areaServed: { '@type': 'City', name: 'Bengaluru' },
        }}
      />
      <PropitstopNav />

      {/* HERO */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16 grid lg:grid-cols-[1fr,1.35fr] gap-8 lg:gap-14 items-center">
        <div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight text-zinc-900">
            Everything<br />your car needs.<br />
            <span className="text-blue-600">Buy. Service. Trust.</span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-zinc-500 max-w-md">
            Find trusted dealers, workshops and experts all in one place.
          </p>

          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link
              to="/used-cars"
              data-testid="home-cta-explore"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold transition-colors shadow-lg shadow-blue-500/20"
            >
              Explore Used Cars <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/service"
              data-testid="home-cta-service"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-white border border-zinc-300 hover:border-zinc-500 text-zinc-800 text-sm font-bold transition-colors"
            >
              Book a Service
            </Link>
          </div>
        </div>

        {/* Hero image — cinematic 16:10 canvas with soft gradient frame */}
        <div className="relative">
          <div className="absolute -inset-3 sm:-inset-4 bg-gradient-to-br from-blue-100 via-white to-orange-100 rounded-[36px] blur-xl opacity-70" />
          <div className="relative rounded-[28px] bg-gradient-to-br from-blue-50 via-white to-orange-50 p-2 sm:p-3 shadow-2xl shadow-blue-200/40 border border-white/70">
            <img
              src={heroCar}
              alt="Everything your car needs"
              fetchPriority="high"
              decoding="async"
              className="w-full aspect-[16/10] object-contain bg-white rounded-[22px]"
              data-testid="home-hero-image"
            />
          </div>
        </div>
      </section>

      {/* TWO PRODUCT TILES — Estimate Check hidden per product decision (Aug 8) */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid md:grid-cols-2 gap-4 lg:gap-6" data-testid="product-tiles">
        <ProductTile
          testid="tile-buy-cars"
          to="/used-cars"
          cta="Explore Cars"
          title={<>Buy a<br />Used Car</>}
          desc="Explore verified used cars from trusted dealers."
          bg="bg-blue-50"
          image={buyCarsTile}
          ctaBg="bg-blue-600 hover:bg-blue-700"
        />
        <ProductTile
          testid="tile-service"
          to="/service"
          cta="Book Service"
          title={<>Service<br />Your Car</>}
          desc="Book car services from trusted workshops."
          bg="bg-emerald-50"
          image={serviceTile}
          ctaBg="bg-emerald-500 hover:bg-emerald-600"
        />
      </section>

      {/* TRUST BADGES STRIP */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 sm:gap-6 items-start">
          {TRUST_BADGES.map((b) => (
            <div key={b.label} className="flex items-start gap-2.5">
              <div className="w-9 h-9 rounded-full bg-zinc-100 flex items-center justify-center flex-shrink-0">
                <b.icon className="w-4 h-4 text-blue-600" />
              </div>
              <div className="text-xs font-medium text-zinc-700 leading-tight pt-1.5">{b.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FRANCHISE — new revenue stream. Admin can swap the image via
          Site Images → home_franchise_image. */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14" data-testid="home-franchise-section">
        <div className="relative overflow-hidden rounded-3xl bg-white border border-orange-100">
          <img
            src={franchiseImg}
            alt="Own a Propitstop Franchise"
            loading="lazy"
            decoding="async"
            className="w-full h-auto block"
            data-testid="home-franchise-image"
          />
          <div className="sm:absolute sm:inset-x-0 sm:bottom-0 flex justify-start p-4 sm:p-6 lg:p-8">
            <Link
              to="/franchise"
              data-testid="home-franchise-cta"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-bold transition-colors shadow-lg shadow-black/20"
            >
              Explore Franchise Opportunity <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* FOR BUSINESSES — re-enabled per product decision (Feb 24, 2026) */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14" data-testid="home-business-section">
        <div className="grid lg:grid-cols-[1fr,2fr] gap-8 items-center bg-blue-50/50 rounded-3xl p-6 lg:p-10">
          <div>
            <div className="text-xs uppercase tracking-widest text-blue-600 font-bold">For Automotive Businesses</div>
            <h2 className="mt-2 text-3xl lg:text-4xl font-extrabold text-zinc-900 leading-tight tracking-tight">
              Grow your automotive business with Propitstop
            </h2>
            <p className="mt-3 text-sm text-zinc-500 leading-relaxed max-w-md">
              Join thousands of dealers, garages and spare parts businesses growing with our AI-powered platforms and digital solutions.
            </p>
            <Link
              to="/business"
              data-testid="business-cta"
              className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-zinc-900 hover:bg-zinc-800 text-white text-sm font-bold transition-colors"
            >
              Explore Business Solutions <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {BUSINESS.map((b) => (
              <div
                key={b.label}
                data-testid={`biz-partner-${b.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`rounded-2xl p-6 border flex flex-col text-left transition-colors ${b.bg}`}
              >
                <div className={`font-display text-xl sm:text-[22px] font-extrabold leading-tight tracking-tight ${b.accent}`}>
                  {b.label}
                </div>
                {!b.live && (
                  <span className="mt-2 inline-block self-start px-2 py-0.5 rounded-full bg-white/70 text-orange-600 text-[10px] font-bold uppercase tracking-wider border border-orange-100">
                    Coming Soon
                  </span>
                )}
                <div className="mt-3 text-sm text-zinc-600 leading-relaxed">{b.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* "Why choose Propitstop" stats strip intentionally removed —
          we'll bring it back once we have real, defensible numbers. */}

      <PropitstopFooter />
    </div>
  );
}

function ProductTile({ testid, to, cta, title, desc, icon, iconBg, bg, image, ctaBg, badge }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`relative ${bg} rounded-3xl p-6 lg:p-7 overflow-hidden flex flex-col justify-between min-h-[260px] group hover:shadow-lg transition-shadow`}
    >
      {/* Content column is explicitly capped so long descriptions can't
          slide under the absolutely-positioned car photo on the right.
          On mobile the image is a bit smaller (52%) and the text column
          gets the remaining ~48% to prevent CTA overlap. */}
      <div className="relative z-10 w-[54%] sm:max-w-[38%] pr-2">
        {icon && (
          <div className={`w-11 h-11 rounded-full ${iconBg} flex items-center justify-center mb-4`}>{icon}</div>
        )}
        <h3 className="text-2xl font-extrabold text-zinc-900 leading-tight tracking-tight">{title}</h3>
        <p className="mt-2 text-sm text-zinc-600">{desc}</p>
        {badge && (
          <div className="mt-3 text-2xl font-extrabold text-orange-600">{badge}</div>
        )}
        <div className={`mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-xl ${ctaBg} text-white text-sm font-bold transition-colors`}>
          {cta} <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
        </div>
      </div>
      <img
        src={image}
        alt=""
        aria-hidden="true"
        className="absolute bottom-0 right-0 w-[46%] sm:w-[60%] h-auto object-contain rounded-tl-2xl group-hover:scale-[1.04] transition-transform duration-500 pointer-events-none"
        style={{ maxHeight: '100%' }}
      />
    </Link>
  );
}
